/**
 * Per-user NUMERALS preference — Arabic-Indic (٠١٢٣٤٥٦٧٨٩) vs Latin (0123456789)
 * digit shaping for the Arabic UI.
 *
 * The founder-decided default is Arabic-Indic in Arabic (matching money + dates),
 * but a user may prefer Western/Latin digits while keeping the Arabic language.
 * This module is the single source of truth for that choice. Every digit-shaping
 * path reads it:
 *   - `intlLocale` / `intlDateLocale` (counts, dates) append `-u-nu-latn` in latin mode
 *   - ICU plural/number formatting (via `parseLngForICU` → `intlLocale`)
 *   - money (`formatMoney` in `src/format/`) reads the mode off the shared
 *     globalThis key below — `src/format/` is the pure `@linkhr/i18n/format`
 *     subpath and must not import this module (it pulls in i18next)
 *
 * English is always Latin; the preference only affects Arabic.
 *
 * Persistence: localStorage is the synchronous first-paint store (read at module
 * load); the server value (GET /me/preferences) reconciles after auth and wins
 * (see `usePrefsReconcile`). Resolution precedence: user preference > default
 * (arabic-indic for ar).
 */
import i18next from "i18next";

export type NumeralsMode = "arabic-indic" | "latin";

const STORAGE_KEY = "linkhr:numerals";

/**
 * Cross-module channel. The money formatter in the pure `src/format/` subpath
 * and the design system's date primitives (a leaf package) read this exact
 * globalThis key to shape digits without importing this module. Keep the string
 * identical everywhere — see `packages/i18n/src/format/money.ts` and
 * `packages/design-system/src/primitives/date/CalendarDate.tsx`.
 */
const GLOBAL_KEY = "__linkhrNumeralsMode__";

function isMode(v: unknown): v is NumeralsMode {
  return v === "arabic-indic" || v === "latin";
}

function readInitial(): NumeralsMode {
  try {
    if (typeof localStorage !== "undefined") {
      const v = localStorage.getItem(STORAGE_KEY);
      if (isMode(v)) return v;
    }
  } catch {
    // localStorage can throw (privacy mode, disabled storage) — fall through.
  }
  return "arabic-indic";
}

let current: NumeralsMode = readInitial();

// Publish immediately so non-i18next readers (money in src/format) see the resolved
// value from first paint, before any React render.
function publishGlobal(mode: NumeralsMode): void {
  (globalThis as Record<string, unknown>)[GLOBAL_KEY] = mode;
}
publishGlobal(current);

/** The active numerals mode (`"arabic-indic"` default for Arabic). */
export function getNumeralsMode(): NumeralsMode {
  return current;
}

export interface SetNumeralsOptions {
  /**
   * Persist to localStorage (default true). Cross-tab / server-reconcile
   * appliers may keep it true too — writing localStorage never re-broadcasts,
   * so there is no echo loop.
   */
  persist?: boolean;
}

/**
 * Set the active numerals mode. Applies synchronously and re-renders the app.
 *
 * The re-render uses a dedicated `numeralsChanged` i18next event that
 * `useTranslation` binds to (see `initI18n`'s `react.bindI18n`). We deliberately
 * do NOT reuse `languageChanged` — the app invalidates the whole TanStack Query
 * cache on a real language switch, and a digit-shaping change must not trigger a
 * network refetch storm.
 */
export function setNumeralsMode(mode: NumeralsMode, opts: SetNumeralsOptions = {}): void {
  current = mode;
  publishGlobal(mode);
  if (opts.persist !== false) {
    try {
      if (typeof localStorage !== "undefined") localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // Ignore storage write failures (privacy mode) — in-memory value still applies.
    }
  }
  // The ICU post-processor memoizes one IntlMessageFormat per (lng, ns, key);
  // that memo key does NOT include the numerals mode, so a cached formatter
  // would keep shaping the previous digits. Clear it so plural strings reformat.
  const icu = (i18next as unknown as { ICU?: { clearCache?: () => void } }).ICU;
  icu?.clearCache?.();
  // Re-render every `useTranslation` consumer so number call sites re-read the
  // numerals-aware formatters.
  i18next.emit("numeralsChanged", mode);
}
